import React, { useState } from "react";
import FlagIcon from "@mui/icons-material/Flag";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import Logger from "../../../services/Logger";
import AdminWordWheelService from "./AdminWordWheelService";
import { missingClueWords } from "./wordWheelDisplayClueUtils";

const BANNED_CATEGORY_REASONS = {
    profanity: "Profanity",
    violence: "Violence",
    drugs: "Drugs",
    offensive: "Offensive",
    inappropriate: "Inappropriate",
    high_frequency: "High-frequency word (too common)",
    other: "Other",
};

function formatBannedReason(banned) {
    const category = String(banned?.category || "").trim().toLowerCase();
    const reason = BANNED_CATEGORY_REASONS[category]
        || category.split("_").filter(Boolean).map((part) => part.charAt(0).toUpperCase() + part.slice(1)).join(" ")
        || "Banned";
    if (banned?.isKids) {
        return `${reason} (kids)`;
    }
    return reason;
}

export default function WordWheelMissingClueFlag({
    row,
    onFill,
    filling = false,
    size = "small",
}) {
    const missing = missingClueWords(row);
    const [checking, setChecking] = useState(false);
    const [bannedHits, setBannedHits] = useState([]);
    const [dialogOpen, setDialogOpen] = useState(false);

    if (missing.length === 0 && !dialogOpen) {
        return null;
    }

    const label = missing.length === 1
        ? `Missing clue for ${missing[0]} — click to fill from the dictionary`
        : `Missing clues for ${missing.join(", ")} — click to fill from the dictionary`;
    const busy = filling || checking;

    const closeDialog = () => {
        setDialogOpen(false);
        setBannedHits([]);
    };

    const acceptFill = () => {
        closeDialog();
        onFill?.(row, missing);
    };

    const handleFlagClick = async (event) => {
        event.stopPropagation();
        event.preventDefault();
        if (busy || !row?.id) {
            return;
        }
        setChecking(true);
        try {
            const hits = await AdminWordWheelService.lookupBannedHitsForTemplate(row);
            if (Array.isArray(hits) && hits.length > 0) {
                setBannedHits(hits);
                setDialogOpen(true);
                return;
            }
            onFill?.(row, missing);
        } catch (error) {
            Logger.error("Failed to check banned words before filling clues:", error);
            onFill?.(row, missing);
        } finally {
            setChecking(false);
        }
    };

    return (
        <>
            {missing.length > 0 ? (
                <Tooltip title={label} arrow>
                    <span style={{ display: "inline-flex" }}>
                        <IconButton
                            size={size}
                            color="error"
                            disabled={busy || !row?.id}
                            onClick={handleFlagClick}
                            aria-label={label}
                            sx={{ p: 0.5, minWidth: 44, minHeight: 44 }}
                        >
                            {busy
                                ? <CircularProgress size={18} color="inherit" />
                                : <FlagIcon fontSize="small" />}
                        </IconButton>
                    </span>
                </Tooltip>
            ) : null}
            <Dialog
                open={dialogOpen}
                onClose={closeDialog}
                onClick={(event) => event.stopPropagation()}
                aria-labelledby="ww-banned-words-title"
                maxWidth="xs"
                fullWidth
            >
                <DialogTitle id="ww-banned-words-title">Banned words</DialogTitle>
                <DialogContent>
                    <Typography variant="body2" sx={{ mb: 1.5 }}>
                        This puzzle includes banned words. Accept to fill missing clues from the dictionary, or cancel.
                    </Typography>
                    <Box sx={{ display: "flex", flexDirection: "column", gap: 0.75 }}>
                        {bannedHits.map((hit, index) => (
                            <Typography key={`${hit.word}-${hit.category}-${index}`} variant="body2">
                                <Box component="span" sx={{ fontWeight: 700 }}>{hit.word}</Box>
                                {" — "}
                                {formatBannedReason(hit)}
                            </Typography>
                        ))}
                    </Box>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
                    <Button
                        onClick={closeDialog}
                        color="inherit"
                        sx={{ minHeight: 44, textTransform: "none" }}
                    >
                        Cancel
                    </Button>
                    <Button
                        onClick={acceptFill}
                        variant="contained"
                        color="error"
                        sx={{ minHeight: 44, textTransform: "none" }}
                    >
                        Accept
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
}
